import {
  Component,
  Input,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort } from '@angular/material/sort';
import { RequestRoot } from 'types';
import { FeaturesImpl } from '../model/features-impl';
import { AppContext } from '../control/context';
import { TableItem, TableData, TableOptions } from './table';

@Component({
  selector: 'i-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  host: { class: 'FLEX' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent implements OnInit, OnChanges {
  @Input() template: any;
  @Input() hideSort: boolean;
  @Input() hideUID: boolean;
  @Input() noScroll: boolean;
  @Input() noCortege: boolean;
  @Input() data: TableData<RequestRoot>;
  @Input() options: TableOptions;
  @Output() optionsChange = new EventEmitter<boolean>();
  @Output() selectionChange = new EventEmitter();

  @ViewChild(MatMenuTrigger, { static: true }) menuRef: MatMenuTrigger;
  @ViewChild(MatSort, { static: true }) sortRef: MatSort;

  contextMenuPosition = { x: '0px', y: '0px' };

  corteges: Array<string>;
  dynamic_columns: Array<string>;
  columns: Array<string>;
  width: string;

  private i18n_cache = new Map<string, string>();
  private flex_cache = new Map<string, string>();

  private supressOnSort = false;

  constructor(private appContext: AppContext) {}

  ngOnInit() {
    this.corteges = Array.from(FeaturesImpl.getColumnCorteges(this.template));
    if (!this.options.cortege && this.corteges.length > 0) {
      this.options.cortege = this.corteges[0];
      this.activateCortege();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['options']) {
      this.activateCortege();
    }
  }

  onSort(e: Sort) {
    this.options.sortArg = e.active;
    this.options.sortDir = e.direction;
    if (!this.supressOnSort) {
      this.optionsChange.emit(true);
    }
  }

  onPage(e: PageEvent) {
    this.options.pageIndex = e.pageIndex;
    this.options.pageSize = e.pageSize;
    this.optionsChange.emit(true);
  }

  onSelect() {
    this.selectionChange.emit();
  }

  private doFormatCaption(caption: string, cb: () => string) {
    const cached = this.i18n_cache.get(caption);
    if (cached !== undefined) {
      return cached;
    }
    const value = this.appContext.getI18NString(cb());
    this.i18n_cache.set(caption, value);
    return value;
  }

  formatStaticColumn(column: string): string {
    return this.doFormatCaption(column, () => 'COLUMN.' + column);
  }

  formatDynamicColumn(column: string): string {
    return this.doFormatCaption(column, () => 'COLUMN.' + this.template.constructor.name + '.' + column);
  }

  formatCortege(cortege: string): string {
    return this.doFormatCaption(cortege, () => 'CORTEGE.' + this.template.constructor.name + '.' + cortege);
  }

  private doFormatCell(row: TableItem<RequestRoot>, column: string): string {
    if (column === '.UID') {
      return row.item.uid!;
    }
    if (column === '.STATUS') {
      return this.appContext.getI18NString('STATUS.' + this.template.constructor.name + '.' + row.item.status);
    }
    return FeaturesImpl.makeString(row.item, column, this.appContext);
  }

  formatCell(row: TableItem<RequestRoot>, column: string): string {
    const cached = row.cache.get(column);
    if (cached !== undefined) {
      return cached;
    }
    const value = this.doFormatCell(row, column);
    row.cache.set(column, value);
    return value;
  }

  formatFlex(column: string): string {
    const cached = this.flex_cache.get(column);
    if (cached !== undefined) {
      return cached;
    }
    const flex = FeaturesImpl.getColumnViewFlexStyle(this.template, this.options.cortege, column);
    this.flex_cache.set(column, flex);
    return flex;
  }

  configureColumns(e: MouseEvent) {
    e.preventDefault();
    this.contextMenuPosition.x = e.clientX + 'px';
    this.contextMenuPosition.y = e.clientY + 'px';
    if (this.corteges.length > 1 && !this.noCortege) {
      this.menuRef.openMenu();
    }
  }

  isCortege(cortege: string): boolean {
    return cortege === this.options.cortege;
  }

  private activateCortege() {
    this.width = FeaturesImpl.getColumnViewFlexWidth(this.template, this.options.cortege, 54 + (this.hideUID ? 0 : 220) + 140);
    this.dynamic_columns = FeaturesImpl.getColumns(this.template, this.options.cortege);
    this.columns = (this.hideUID ? ['.select', '.status'] : ['.select', '.uid', '.status']).concat(this.dynamic_columns);
  }

  goCortege(cortege: string) {
    this.supressOnSort = true;
    this.sortRef.sort({ id: '', start: 'asc', disableClear: false });
    this.supressOnSort = false;
    this.options.cortege = cortege;
    this.activateCortege();
    this.optionsChange.emit(true);
  }

  toggleRows() {
    this.data.toggle();
    this.onSelect();
  }

  toggleRow(row: TableItem<RequestRoot>) {
    this.data.selection.toggle(row);
    this.onSelect();
  }

  selectRow(row: TableItem<RequestRoot>) {
    this.data.select(row);
    this.onSelect();
  }
}
